import { Link } from "react-router-dom";
import { CloudDownload, Play, Trash2, WifiOff, Disc, ListMusic, Heart, RefreshCw } from "lucide-react";
import { setAutoFavorites, setAutoPlaylist, isAutoPlaylist } from "@/lib/autoPins";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Cover } from "@/components/Cover";
import { TrackTable } from "@/components/TrackTable";
import { usePlayer } from "@/context/PlayerContext";
import { useAuth } from "@/context/AuthContext";
import { useOffline, unpin, clearAll, usedBytes, getCapMB, fmtBytes, fmtCap } from "@/lib/offline";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useState } from "react";

const PinCard = ({ p, onRemove }) => {
  const player = usePlayer();
  const [open, setOpen] = useState(false);
  const cover = p.coverAlbumIds?.[0] || p.tracks[0]?.album_id;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid={`offline-pin-${p.key}`}>
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md">
          {cover ? <Cover albumId={cover} alt={p.name} className="h-16 w-16" iconSize={20} /> : <div className="flex h-16 w-16 items-center justify-center bg-zinc-100 text-zinc-400">{p.type === "favorites" ? <Heart size={20} /> : <ListMusic size={20} />}</div>}
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-700">
            {p.type === "album" ? <Disc size={11} /> : p.type === "favorites" ? <Heart size={11} /> : <ListMusic size={11} />}{p.type}
            {p.auto && <span className="flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-mono text-[9px] normal-case tracking-normal text-emerald-700" data-testid={`offline-auto-badge-${p.key}`}><RefreshCw size={9} />Auto-synced</span>}
          </p>
          <Link to={p.type === "album" ? `/album/${p.id}` : p.type === "favorites" ? "/favorites" : `/playlists/${p.id}`} className="block truncate text-base font-medium hover:underline" data-testid={`offline-pin-name-${p.key}`}>{p.name}</Link>
          <p className="truncate font-mono text-[11px] text-zinc-500">{p.subtitle ? `${p.subtitle} · ` : ""}{p.tracks.length} song{p.tracks.length === 1 ? "" : "s"} · {fmtBytes(p.bytes)}</p>
        </div>
        <button onClick={() => player.playTracks(p.tracks)} data-testid={`offline-play-${p.key}`} className="flex h-10 items-center gap-2 rounded-full bg-zinc-900 px-4 text-sm text-white hover:bg-zinc-700"><Play size={14} fill="currentColor" />Play</button>
        <button onClick={() => setOpen((o) => !o)} data-testid={`offline-toggle-${p.key}`} className="h-10 rounded-full border border-zinc-300 px-4 text-sm hover:border-zinc-900">{open ? "Hide songs" : "Songs"}</button>
        <button onClick={onRemove} data-testid={`offline-remove-${p.key}`} title="Remove from this device" className="flex h-10 w-10 items-center justify-center rounded-full text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
      </div>
      {open && <div className="mt-4"><TrackTable tracks={p.tracks} readOnly showAlbum={p.type !== "album"} /></div>}
    </div>
  );
};

export default function Offline() {
  const { pins, loaded } = useOffline();
  const { user } = useAuth();
  const online = useOnlineStatus();
  const mine = pins.filter((p) => !p.userId || p.userId === user?.id).sort((a, b) => (a.pinned_at < b.pinned_at ? 1 : -1));
  const remove = async (p) => {
    const wasAuto = p.type === "favorites" || isAutoPlaylist(p.id);
    if (p.type === "favorites") setAutoFavorites(false);
    else if (isAutoPlaylist(p.id)) setAutoPlaylist(p.id, false, true);
    else await unpin(p.type, p.id);
    toast.success(`${p.name} removed from this device${wasAuto ? " — auto-sync turned off" : ""}`);
  };
  const removeAll = async () => {
    if (!window.confirm(`Remove all ${mine.length} saved items (${fmtBytes(usedBytes())}) from this device? Auto-sync for favorites and playlists is turned off too.`)) return;
    setAutoFavorites(false);
    mine.forEach((p) => { if (p.type === "playlist" && isAutoPlaylist(p.id)) setAutoPlaylist(p.id, false, true); });
    await clearAll();
    toast.success("All offline music removed from this device");
  };
  return (
    <div>
      <PageHeader eyebrow="Library" title="Offline music" testid="offline-title">
        <div className="flex items-center gap-3 text-sm text-zinc-500">
          {!online && <span className="flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800" data-testid="offline-banner"><WifiOff size={12} />You're offline — only saved music will play</span>}
          <span className="font-mono text-xs" data-testid="offline-usage">{fmtBytes(usedBytes())} of {fmtCap(getCapMB())} used · {mine.length} item{mine.length === 1 ? "" : "s"}</span>
          {mine.length > 0 && <button onClick={removeAll} data-testid="offline-clear-all" className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 bg-white px-4 text-xs text-zinc-700 hover:border-red-500 hover:text-red-600"><Trash2 size={13} />Clear all</button>}
        </div>
      </PageHeader>
      {loaded && !mine.length ? (
        <EmptyState icon={CloudDownload} title="Nothing saved for offline yet" text="Open any album or playlist and press “Save offline”. Songs are stored on this device and keep playing without a connection." />
      ) : (
        <div className="space-y-4" data-testid="offline-list">{mine.map((p) => <PinCard key={p.key} p={p} onRemove={() => remove(p)} />)}</div>
      )}
    </div>
  );
}
